import { useState } from 'react';

import { AppIcon, ChartCard, College, BarChartProps, addHttpIfMissing } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Typography, Box, SxProps, Theme, Button } from '@mui/material';

import { LineIconItem } from '../atoms/LineIconItem';
import {
  calculateExtracurricularsFromAdmissionRate,
  calculateRigorFromAdmissionRate,
} from '../college.util';

const WHO_GETS_IN_STRINGS = appStrings.modules.college.whoGetsIn;

type WhoGetsInProps = Readonly<{
  college: College;
}>;

export const WhoGetsIn = ({ college }: WhoGetsInProps) => {
  const [isSATOrACT, setIsSATOrACT] = useState<'SAT' | 'ACT'>(college.actcm25 ? 'ACT' : 'SAT');

  const SATData: BarChartProps = {
    chartMin: 400,
    chartMax: 1600,
    ticks: [400, 800, 1200, 1600],
    dataMin: (college.satmt25 ?? 0) + (college.satvr25 ?? 0),
    dataAverage: (college.satmt50 ?? 0) + (college.satvr50 ?? 0),
    dataMax: (college.satmt75 ?? 0) + (college.satvr75 ?? 0),
  };

  const ACTData: BarChartProps = {
    chartMin: 1,
    chartMax: 36,
    ticks: [1, 9, 18, 27, 36],
    dataMin: college.actcm25 ?? 0,
    dataAverage: college.actcm50 ?? 0,
    dataMax: college.actcm75 ?? 0,
  };

  const GPAData: BarChartProps = {
    chartMin: 0,
    chartMax: 4.5,
    ticks: [0, 1, 2, 3, 4],
    dataMin: college.gpa_25 ?? 0,
    dataAverage: college.gpa_50 ?? 0,
    dataMax: college.gpa_75 ?? 0,
  };

  return (
    <Box sx={containerSx}>
      {/* TITLE AND ADMISSIONS WEBSITE BUTTON */}
      <Box sx={titleContainerSx}>
        <Box>
          <Typography component="div" variant="display-sm-medium">
            {WHO_GETS_IN_STRINGS.header.title}
          </Typography>

          <Typography variant="text-sm-regular">{WHO_GETS_IN_STRINGS.header.subtitle}</Typography>
        </Box>

        {(college.application_url || college.url) && (
          <Button
            hierarchy="Secondary gray"
            size="md"
            startIcon={
              <AppIcon iconName="Share06" diameter="20px" color="Colors/Text/text-secondary" />
            }
            onClick={() => {
              window.open(addHttpIfMissing(college.application_url || college.url), '_blank');
            }}
          >
            {WHO_GETS_IN_STRINGS.header.admissionsButton}
          </Button>
        )}
      </Box>

      {/* ACCEPTANCE RATE AND EARLY DECISION TIME */}
      <Box>
        <Box sx={firstRowChartContainerSx}>
          <ChartCard
            chartVariant={{
              type: 'DONUT',
              donut: { percentageFilled: (college.admission_rate || 0) * 100 },
            }}
            additionalInfoVariant={{
              type: 'TOOLTIP',
              toolTipText: WHO_GETS_IN_STRINGS.charts.acceptanceRate.tooltip,
            }}
            title={WHO_GETS_IN_STRINGS.charts.acceptanceRate.title}
          />

          <Box sx={deadlinesContainerSx}>
            {/* We have to wrap the line items in a container to align the items properly */}
            <Box sx={lineItemsContainerSx}>
              {college.early_decision_deadline && (
                <LineIconItem
                  title={WHO_GETS_IN_STRINGS.earlyDecision}
                  iconName="CalendarDate"
                  text={`${new Date(college.early_decision_deadline).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}`}
                />
              )}

              {college.application_deadline && (
                <LineIconItem
                  title={WHO_GETS_IN_STRINGS.applicationDeadline}
                  iconName="CalendarDate"
                  text={`${new Date(college.application_deadline).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}`}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* HIGH SCHOOL GPA AND SAT SCORE */}
        <Box sx={secondRowChartContainerSx}>
          <ChartCard
            title={WHO_GETS_IN_STRINGS.charts.highSchoolGPA.title}
            additionalInfoVariant={{
              type: 'TOOLTIP',
              toolTipText: WHO_GETS_IN_STRINGS.charts.highSchoolGPA.tooltip,
            }}
            chartVariant={{
              type: 'BAR',
              bar: GPAData,
            }}
          />

          {/* inlining the conditionals here gets us a nicer animation when the type changes */}
          <ChartCard
            title={WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].title}
            additionalInfoVariant={{
              type: 'TOOLTIP',
              toolTipText:
                WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].tooltip,
            }}
            headerAction={{
              label:
                WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].switch,
              icon: 'RefreshCcw05',
              onClick: () => setIsSATOrACT((prev) => (prev === 'SAT' ? 'ACT' : 'SAT')),
            }}
            chartVariant={{
              type: 'BAR',
              bar: isSATOrACT === 'SAT' ? SATData : ACTData,
            }}
          />
        </Box>

        {/* RIGOR AND EXTRACURRICULAR ACTIVITIES */}
        <Box sx={thirdRowChartContainerSx}>
          <ChartCard
            title={WHO_GETS_IN_STRINGS.charts.rigor.title}
            additionalInfoVariant={{
              type: 'MODAL',
              modalData: WHO_GETS_IN_STRINGS.charts.rigor.modal,
            }}
            chartVariant={{
              type: 'PROGRESS_CIRCLE',
              text: calculateRigorFromAdmissionRate(college.admission_rate || 0).rigor,
              progressCircle: {
                percent: calculateRigorFromAdmissionRate(college.admission_rate || 0).percent,
              },
            }}
          />

          <ChartCard
            title={WHO_GETS_IN_STRINGS.charts.extracurriculars.title}
            additionalInfoVariant={{
              type: 'MODAL',
              modalData: WHO_GETS_IN_STRINGS.charts.extracurriculars.modal,
            }}
            chartVariant={{
              type: 'PROGRESS_CIRCLE',
              text: calculateExtracurricularsFromAdmissionRate(college.admission_rate || 0)
                .extracurriculars,
              progressCircle: {
                percent: calculateExtracurricularsFromAdmissionRate(college.admission_rate || 0)
                  .percent,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  mb: backrs.spacing['spacing-6xl'],
  marginX: backrs.spacing['spacing-4xl'],
  '@media (max-height: 768px)': {
    mx: backrs.spacing['spacing-4xl'],
  },
  '@media (max-width: 414px)': {
    mx: backrs.spacing['spacing-2xl'],
  },
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: backrs.spacing['spacing-2xl'],
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-2xl'],
});

const firstRowChartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: {
    xs: 'column-reverse',
    lg: 'row',
  },
  gap: backrs.spacing['spacing-3xl'],
  flexWrap: { xs: 'wrap', lg: 'unset' },
});

const deadlinesContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: {
    xs: 'flex-start',
    lg: 'center',
  },
  width: '100%',
};

const lineItemsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const secondRowChartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: backrs.spacing['spacing-3xl'],
  gap: backrs.spacing['spacing-3xl'],
  flexDirection: {
    xs: 'column',
    sm: 'row',
  },
  flexWrap: { xs: 'wrap', lg: 'unset' },
});

const thirdRowChartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: backrs.spacing['spacing-3xl'],
  gap: backrs.spacing['spacing-3xl'],
  flexDirection: {
    xs: 'column',
    sm: 'row',
  },
  flexWrap: { xs: 'wrap', lg: 'unset' },
});
